import { useEffect, useRef, useState } from "react";
import { RobotFilled, MinusOutlined, WechatFilled} from "@ant-design/icons";
import axios from "axios";
import SendIcon from "./icons/send-icon.svg";
import LoadingIcon from "./icons/loading-icon.svg";
import UserIcon from "./icons/user-icon.svg";

import styles from "./chatbot.module.css";

const MAX_MESSAGES = 10;

const ChatBot = ({ isChatOpen, handleChatChange }) => {
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [chatMessages, setChatMessages] = useState([
    { role: "assistant", content: "Σε τι μπορώ να βοηθήσω;" },
  ]);

  const [messagesSend, setMessagesSend] = useState(0);

  const chatAreaRef = useRef(null);

  // Scroll to bottom whenever chatMessages changes
  useEffect(() => {
    if (chatAreaRef.current) {
      setTimeout(() => {
        chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
      }, 50);
    }
  }, [chatMessages, loading]);

  const handleChatPrompt = async (messages) => {
    setLoading(true);
    let chatResponse = null;

    // Filter out invalid messages
    const sanitizedMessages = messages.filter(
      (msg) => msg.content && typeof msg.content === "string",
    );

    try {
      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-3.5-turbo",
          messages: sanitizedMessages,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_CHATBOT}`,
          },
        },
      );
      chatResponse = response.data.choices[0].message.content;
    } catch (error) {
      console.error("Error fetching ChatGPT response:", error);
      chatResponse =
        "Υπάρχει κάποιο πρόβλημα με το σύστημα. Παρακαλώ δοκιμάστε αργότερα.";
    } finally {
      setLoading(false);
    }

    setChatMessages([...messages, { role: "assistant", content: chatResponse }]);
  };

  const isDisabled = prompt.trim() === "" || messagesSend >= MAX_MESSAGES;

  const handleSubmit = () => {
    if (prompt.trim() === "") return;

    const tempMessages = [...chatMessages, { role: "user", content: prompt }];

    setChatMessages(tempMessages);
    setMessagesSend((prev) => prev + 1);
    handleChatPrompt(tempMessages);
    setPrompt("");
  };

  const renderMessages = () =>
    chatMessages.map((item, id) => {
      const isAssistant = item.role === "assistant";
      const roleClass = isAssistant ? styles.assistant : "";

      return (
        <div key={id} className={`${styles.messageWrapper} ${roleClass}`}>
          <div className={`${styles.messageContainer} ${roleClass}`}>
            <div className={styles.messageAvatar}>
              {isAssistant ? (
                <RobotFilled />
              ) : (
                <img className={styles.userLogo} src={UserIcon} alt="" />
              )}
            </div>
            <p className={`${styles.message} ${roleClass}`}>{item.content}</p>
          </div>
        </div>
      );
    });

  const openClass = isChatOpen ? styles.open : "";

  return (
    <>
      {/* Κουμπί ανοίγματος: όταν το chat κλείνει συρρικνώνεται σε μηδενικό
          μέγεθος, οπότε χρειάζεται ξεχωριστό σημείο για να ξανανοίξει */}
      {!isChatOpen && (
        <button
          type="button"
          className={styles.openButton}
          onClick={() => handleChatChange()}
          aria-label="Άνοιγμα συνομιλίας"
        >
          <WechatFilled />
        </button>
      )}

      <div className={`${styles.chatBox} ${openClass}`}>
        <div className={`${styles.chatTitle} ${openClass}`}>
          <div className={`${styles.iconArea} ${openClass}`}>
            <RobotFilled />
          </div>

          <div className={styles.textArea}>
            <p className={`${styles.title} ${openClass}`}>Explorechania Chat</p>
            <p className={`${styles.subTitle} ${openClass}`}>
              Powered By Chat GPT
            </p>
          </div>

          <button
            type="button"
            className={`${styles.closeButton} ${openClass}`}
            onClick={() => handleChatChange()}
            aria-label="Κλείσιμο συνομιλίας"
          >
            <MinusOutlined />
          </button>
        </div>

        <div className={styles.chatArea} ref={chatAreaRef}>
          <div className={styles.messagesArea}>
            {renderMessages()}

            {loading && (
              <img className={styles.loadingMessage} src={LoadingIcon} alt="" />
            )}

            <span className={styles.messageInfo}>
              Διαθέσιμα μηνύματα: {MAX_MESSAGES - messagesSend}
            </span>
          </div>

          <div className={styles.inputArea}>
            <textarea
              className={styles.inputPrompt}
              value={prompt}
              placeholder={
                messagesSend >= MAX_MESSAGES
                  ? "Έχετε φτάσει το όριο μηνυμάτων ανά συνεδρία"
                  : "Ρώτησε κάτι"
              }
              onChange={(event) => setPrompt(event.target.value)}
              disabled={messagesSend >= MAX_MESSAGES}
            />

            <button
              type="button"
              className={styles.submitButton}
              disabled={isDisabled}
              onClick={handleSubmit}
            >
              <img
                className={`${styles.submitIcon} ${isDisabled ? styles.disabled : ""}`}
                src={SendIcon}
                alt="Αποστολή"
              />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatBot;